import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Download, FileDown, FileText, Loader2, RefreshCw, Search, ShieldCheck, Wallet2, Zap } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { useAuth } from "../auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Reveal } from "../components/Reveal";
import AdminIntegrations from "../components/AdminIntegrations";
import AdminCredit from "../components/AdminCredit";
import { AdminAiUsage } from "../components/AdminAiUsage";
import AdminCreditFailures from "../components/AdminCreditFailures";
import AdminSecurity from "../components/AdminSecurity";
import AdminConsents from "../components/AdminConsents";
import AdminInvoices from "../components/AdminInvoices";
import AdminSims from "../components/AdminSims";
import AdminBlog from "../components/AdminBlog";
import AdminMargins from "../components/AdminMargins";
import AdminReferrals from "../components/AdminReferrals";
import AdminDealers from "../components/AdminDealers";
import AdminDirectory from "../components/AdminDirectory";
import { AdminIdentity } from "../components/AdminIdentity";
import { AdminMarketing } from "../components/AdminMarketing";
import AdminBonus from "../components/AdminBonus";
import { hasUnsaved, saveUnsaved, discardUnsaved, registerUnsaved, unregisterUnsaved } from "../utils/unsaved";

function Stat({ label, value }) {
  return (
    <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6">
      <div className="font-display font-extrabold text-3xl text-slate-900">{value}</div>
      <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400 mt-1">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const ok = ["completed", "paid", "ACTIVE"].includes(status);
  const wait = ["pending", "initiated", "pending_identity"].includes(status);
  return (
    <span className={`rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-[0.15em] ${ok ? "bg-emerald-50 text-emerald-600" : status === "pending_identity" ? "bg-violet-50 text-violet-600" : wait ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-500"}`}>
      {status}
    </span>
  );
}

function TopupDialog({ sim, open, onOpenChange }) {
  const { t, fmt } = useLanguage();
  const [tops, setTops] = useState(null);
  const [busy, setBusy] = useState(null);
  const [fund, setFund] = useState("airalo");
  const [owner, setOwner] = useState(null);
  const [grantAmt, setGrantAmt] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);

  const refreshOwner = (iccid) => api.get(`/admin/sims/${iccid}/usage`).then(({ data }) => setOwner(data.data)).catch(() => setOwner(null));

  const grantWallet = async () => {
    const amount = parseFloat(grantAmt);
    if (!amount || amount <= 0 || !owner?.owner_email) return;
    setGrantBusy(true);
    try {
      await api.post("/admin/wallet/grant", { email: owner.owner_email, amount, note: "Accredito rapido da ricarica SIM" });
      toast.success(`+€${amount.toFixed(2)} ✓`);
      setGrantAmt("");
      await refreshOwner(sim.iccid);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setGrantBusy(false);
    }
  };

  useEffect(() => {
    if (open && sim) {
      setTops(null);
      setFund("airalo");
      setOwner(null);
      api.get(`/esims/${sim.iccid}/topups`).then(({ data }) => setTops(data.data || [])).catch(() => setTops([]));
      refreshOwner(sim.iccid);
    }
  }, [open, sim]);

  const send = async (p) => {
    setBusy(p.package_id);
    try {
      await api.post(`/admin/esims/${sim.iccid}/topup`, { package_id: p.package_id, fund });
      toast.success(`${t("admin.topup")} ✓ ${p.data || p.title}`);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "insufficient_wallet_balance" ? t("wallet.insufficient") : d === "airalo_credit_pending" ? t("admin.creditPending") : d || t("misc.error"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-topup-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{t("admin.topup")} — {sim?.package_title}</DialogTitle>
        </DialogHeader>
        <div className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-400 break-all">{sim?.iccid}</div>

        <div className="mt-4">
          <div className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t("admin.fundSource")}</div>
          <div className="grid grid-cols-2 gap-2" data-testid="admin-topup-fund">
            <button type="button" data-testid="admin-fund-airalo" onClick={() => setFund("airalo")}
              className={`rounded-2xl border px-4 py-3 text-left transition-colors ${fund === "airalo" ? "border-[#FF6B35] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="font-display font-bold text-sm text-slate-900">{t("admin.fundAiralo")}</div>
              <div className="text-xs text-slate-500">{t("admin.fundAiraloDesc")}</div>
            </button>
            <button type="button" data-testid="admin-fund-wallet" onClick={() => setFund("wallet")}
              className={`rounded-2xl border px-4 py-3 text-left transition-colors ${fund === "wallet" ? "border-[#FF6B35] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="font-display font-bold text-sm text-slate-900">{t("admin.fundWallet")}</div>
              <div className="text-xs text-slate-500">
                {owner ? `${t("admin.customerWallet")}: € ${Number(owner.owner_wallet ?? 0).toFixed(2)}` : "…"}
              </div>
            </button>
          </div>
          {fund === "wallet" && owner && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-[#FAF5EE] px-4 py-2.5" data-testid="admin-topup-grant">
              <span className="text-xs text-slate-500">{t("admin.grantWallet")}:</span>
              <input data-testid="admin-grant-amount" type="number" min="1" step="5" value={grantAmt}
                onChange={(e) => setGrantAmt(e.target.value)} placeholder="50"
                className="w-24 rounded-xl border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
              <button type="button" data-testid="admin-grant-send" onClick={grantWallet} disabled={grantBusy}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1E293B] text-white px-4 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest hover:bg-slate-700 transition-colors disabled:opacity-60">
                {grantBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wallet2 className="w-3 h-3" />}
                {t("admin.grant")}
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 max-h-[46vh] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] pr-1 space-y-2" data-testid="admin-topup-list">
          {tops === null ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
          ) : tops.length === 0 ? (
            <p className="py-6 text-left text-sm text-slate-400">{t("cat.none")}</p>
          ) : (
            tops.map((p) => (
              <div key={p.package_id} className="flex items-center justify-between rounded-xl border border-slate-200/80 px-4 py-2.5">
                <div className="font-display font-bold text-sm text-slate-900">{p.is_unlimited ? t("dest.unlimited") : p.data}</div>
                <div className="flex items-center gap-3">
                  <span className="font-mono-t text-xs font-bold text-[#FF6B35]">{fmt(p.price)}</span>
                  <button data-testid={`admin-topup-send-${p.package_id}`} onClick={() => send(p)} disabled={busy === p.package_id}
                    className="rounded-full bg-[#FF6B35] text-white px-4 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-[0.15em] hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center gap-2">
                    {busy === p.package_id && <Loader2 className="w-3 h-3 animate-spin" />}
                    {t("admin.topup")}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RefundDialog({ order, onClose, onDone }) {
  const { t } = useLanguage();
  const [disableSim, setDisableSim] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/refund`, { disable_sim: disableSim, reason });
      toast.success(`${t("admin.refunded")} (${data.method})`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent data-testid="admin-refund-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{t("admin.refundTitle")}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-600">
          <div className="font-medium text-slate-900">{order?.package_title}</div>
          <div className="text-xs text-slate-400 mt-1">{order?.user_email} · {order?.price} {order?.currency} · {order?.order_id}</div>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">{t("admin.refundHow")}</p>
        <div className="grid grid-cols-2 gap-2" data-testid="admin-refund-sim-choice">
          <button type="button" data-testid="admin-refund-keep-sim" onClick={() => setDisableSim(false)}
            className={`rounded-2xl border px-4 py-3 text-left transition-colors ${!disableSim ? "border-[#FF6B35] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="font-display font-bold text-sm text-slate-900">{t("admin.refundKeep")}</div>
            <div className="text-xs text-slate-500">{t("admin.refundKeepDesc")}</div>
          </button>
          <button type="button" data-testid="admin-refund-disable-sim" onClick={() => setDisableSim(true)}
            className={`rounded-2xl border px-4 py-3 text-left transition-colors ${disableSim ? "border-red-400 bg-red-50" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="font-display font-bold text-sm text-slate-900">{t("admin.refundDisable")}</div>
            <div className="text-xs text-slate-500">{t("admin.refundDisableDesc")}</div>
          </button>
        </div>
        <input data-testid="admin-refund-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder={t("sec.reason")}
          className="w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
        <button data-testid="admin-refund-confirm" onClick={run} disabled={busy}
          className="w-full rounded-full bg-red-500 text-white px-6 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-red-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {t("admin.refundConfirm")}
        </button>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { t, fmt } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const VALID_TABS = ["overview", "customers", "orders", "transactions", "esims", "credit", "failures", "invoices", "margins", "referral", "dealers", "directory", "identity", "blog", "marketing", "tickets", "logs", "integrations", "security", "ai", "settings"];
  const [tab, setTabRaw] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("tab");
    return VALID_TABS.includes(p) ? p : "overview";
  });
  const setTab = (k) => {
    window.history.replaceState(null, "", `/admin?tab=${k}`);
    setTabRaw(k);
  };
  const [searchQ, setSearchQ] = useState("");
  const [custFilter, setCustFilter] = useState("all");
  const [logSearch, setLogSearch] = useState("");
  const [period, setPeriod] = useState("year");
  const [overview, setOverview] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [orders, setOrders] = useState(null);
  const [txs, setTxs] = useState(null);
  const [esims, setEsims] = useState(null);
  const [topupSim, setTopupSim] = useState(null);
  const [esimQuery, setEsimQuery] = useState(() =>
    new URLSearchParams(window.location.search).get("q") || "");  // salto diretto dalla Directory

  // Deep-link reattivo: i Link interni (es. badge ICCID dalla Directory → ?tab=esims&q=…)
  // cambiano la query SENZA ricaricare la pagina: qui sincronizzo tab e ricerca SIM.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get("tab");
    if (t && VALID_TABS.includes(t)) setTabRaw(t);
    const q = p.get("q");
    if (q !== null) setEsimQuery(q);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [refundOrder, setRefundOrder] = useState(null);
  const [sales, setSales] = useState(null);
  const [salesDays, setSalesDays] = useState(30);
  const [airaloBalance, setAiraloBalance] = useState(null);
  const [creditAlert, setCreditAlert] = useState(null);
  const [detail, setDetail] = useState(null);
  const [syncLogs, setSyncLogs] = useState(null);
  const [appLogs, setAppLogs] = useState(null);
  const [logTab, setLogTab] = useState("sync");
  const [logMore, setLogMore] = useState({ app: false, sync: false, client: false });
  const [logBusy, setLogBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [tickets, setTickets] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [ticketBusy, setTicketBusy] = useState(null);
  const [clientErrors, setClientErrors] = useState(null);
  const [copyEmail, setCopyEmail] = useState("");
  const [copyBusy, setCopyBusy] = useState(false);
  const [digestTimes, setDigestTimes] = useState("07:00, 19:00");
  const [emailLoops, setEmailLoops] = useState(false);
  const [emailBlockTests, setEmailBlockTests] = useState(true);
  const [twofaSms, setTwofaSms] = useState(false);
  const [twofaEmail, setTwofaEmail] = useState(false);
  const [twofaAdminReq, setTwofaAdminReq] = useState(false);
  const [quietFrom, setQuietFrom] = useState(23);
  const [quietTo, setQuietTo] = useState(7);
  const [markupPct, setMarkupPct] = useState("0");
  const [blogAuto, setBlogAuto] = useState(false);
  const [blogMaxDay, setBlogMaxDay] = useState("2");
  const [companyForm, setCompanyForm] = useState({ name: "", vat: "", address: "", zip: "", city: "", province: "", country: "", email: "", phone: "" });
  const [settingsBase, setSettingsBase] = useState(null);
  const [unsavedNav, setUnsavedNav] = useState(null);

  const saveSettings = async () => {
    setCopyBusy(true);
    try {
      const { data } = await api.post("/admin/settings", { email_global_copy: copyEmail.trim(), digest_times: digestTimes.trim(), company: companyForm,
        email_loops_enabled: emailLoops, email_block_test_users: emailBlockTests, email_quiet_from: Number(quietFrom), email_quiet_to: Number(quietTo),
        twofa_sms_enabled: twofaSms, twofa_email_enabled: twofaEmail, twofa_admin_mandatory: twofaAdminReq,
        catalog_markup_pct: markupPct === "" ? null : Number(markupPct),
        blog_auto_enabled: blogAuto, blog_max_per_day: blogMaxDay === "" ? null : Number(blogMaxDay) });
      setCopyEmail(data.email_global_copy);
      setDigestTimes(data.digest_times || "07:00, 19:00");
      setMarkupPct(String(data.catalog_markup_pct ?? 0));
      setBlogAuto(!!data.blog_auto_enabled);
      setBlogMaxDay(String(data.blog_max_per_day ?? 2));
      if (data.company) setCompanyForm(data.company);
      setSettingsBase({ email_global_copy: data.email_global_copy || "", digest_times: data.digest_times || "", company: data.company || companyForm,
        emailLoops: !!data.email_loops_enabled, emailBlockTests: data.email_block_test_users !== false, quietFrom: data.email_quiet_from ?? 23, quietTo: data.email_quiet_to ?? 7,
        twofaSms: !!data.twofa_sms_enabled, twofaEmail: !!data.twofa_email_enabled, twofaAdminReq: !!data.twofa_admin_mandatory,
        markupPct: Number(data.catalog_markup_pct ?? 0), blogAuto: !!data.blog_auto_enabled, blogMaxDay: Number(data.blog_max_per_day ?? 2) });
      toast.success(t("admin.saved"));
    } catch (e) {
      if (e.response?.data?.detail === "invalid_catalog_markup") setMarkupPct(String(settingsBase?.markupPct ?? 25));
      if (e.response?.data?.detail === "invalid_blog_max_per_day") setBlogMaxDay(String(settingsBase?.blogMaxDay ?? 2));
      toast.error(t(e.response?.data?.detail === "invalid_email" ? "news.invalid" : e.response?.data?.detail === "invalid_digest_times" ? "admin.digestTimesInvalid" : e.response?.data?.detail === "invalid_catalog_markup" ? "admin.catalogMarkupInvalid" : "misc.error"));
      throw e;
    } finally {
      setCopyBusy(false);
    }
  };

  const LOG_PAGE = 25;

  const loadSyncLogs = async (skip = 0) => {
    setLogBusy(true);
    try {
      const { data } = await api.get(`/admin/sync-logs?skip=${skip}&limit=${LOG_PAGE}`);
      setSyncLogs((old) => (skip === 0 || !old ? data.data || [] : [...old, ...(data.data || [])]));
      setLogMore((m) => ({ ...m, sync: skip + (data.data || []).length < (data.total || 0) }));
    } catch { if (skip === 0) setSyncLogs([]); } finally { setLogBusy(false); }
  };

  const loadAppLogs = async (skip = 0) => {
    setLogBusy(true);
    const cat = { events: "events", errors: "errors", health: "health" }[logTab] || "all";
    try {
      const { data } = await api.get(`/admin/logs?category=${cat}&skip=${skip}&limit=${LOG_PAGE}`);
      setAppLogs((old) => (skip === 0 || !old ? data.data || [] : [...old, ...(data.data || [])]));
      setLogMore((m) => ({ ...m, app: skip + (data.data || []).length < (data.total || 0) }));
    } catch { if (skip === 0) setAppLogs([]); } finally { setLogBusy(false); }
  };

  const loadClientErrors = async (skip = 0) => {
    setLogBusy(true);
    try {
      const { data } = await api.get(`/admin/client-errors?skip=${skip}&limit=${LOG_PAGE}`);
      setClientErrors((old) => (skip === 0 || !old ? data.data || [] : [...old, ...(data.data || [])]));
      setLogMore((m) => ({ ...m, client: skip + (data.data || []).length < (data.total || 0) }));
    } catch { if (skip === 0) setClientErrors([]); } finally { setLogBusy(false); }
  };

  const rollbackSync = async (syncId) => {
    try {
      const { data } = await api.post(`/admin/sync-logs/${syncId}/rollback`);
      toast.success(`${t("admin.rollbackDone")} (${data.packages})`);
      loadSyncLogs(0);
    } catch (e) {
      toast.error(t(e.response?.status === 410 ? "admin.snapshotExpired" : "admin.rollbackFail"));
    }
  };

  useEffect(() => {
    if (user === null) navigate("/account?next=/admin", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => {});
    api.get("/admin/customers").then(({ data }) => setCustomers(data.data || [])).catch(() => setCustomers([]));
    api.get("/admin/orders").then(({ data }) => setOrders(data.data || [])).catch(() => setOrders([]));
    api.get("/admin/transactions").then(({ data }) => setTxs(data.data || [])).catch(() => setTxs([]));
    api.get("/admin/esims").then(({ data }) => setEsims(data.data || [])).catch(() => setEsims([]));
    api.get("/admin/tickets").then(({ data }) => setTickets(data.data || [])).catch(() => setTickets([]));
    api.get("/admin/airalo/balance").then(({ data }) => setAiraloBalance(data.data)).catch(() => {});
    api.get("/admin/credit").then(({ data }) => setCreditAlert(data.data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => {
      setCopyEmail(data.data?.email_global_copy || "");
      setDigestTimes(data.data?.digest_times || "07:00, 19:00");
      setEmailLoops(!!data.data?.email_loops_enabled);
      setEmailBlockTests(data.data?.email_block_test_users !== false);
      setTwofaSms(!!data.data?.twofa_sms_enabled);
      setTwofaEmail(!!data.data?.twofa_email_enabled);
      setTwofaAdminReq(!!data.data?.twofa_admin_mandatory);
      setQuietFrom(data.data?.email_quiet_from ?? 23);
      setQuietTo(data.data?.email_quiet_to ?? 7);
      setMarkupPct(String(data.data?.catalog_markup_pct ?? 0));
      setBlogAuto(!!data.data?.blog_auto_enabled);
      setBlogMaxDay(String(data.data?.blog_max_per_day ?? 2));
      if (data.data?.company) setCompanyForm(data.data.company);
      setSettingsBase({ email_global_copy: data.data?.email_global_copy || "", digest_times: data.data?.digest_times || "", company: data.data?.company || null,
        emailLoops: !!data.data?.email_loops_enabled, emailBlockTests: data.data?.email_block_test_users !== false, quietFrom: data.data?.email_quiet_from ?? 23, quietTo: data.data?.email_quiet_to ?? 7,
        twofaSms: !!data.data?.twofa_sms_enabled, twofaEmail: !!data.data?.twofa_email_enabled, twofaAdminReq: !!data.data?.twofa_admin_mandatory,
        markupPct: Number(data.data?.catalog_markup_pct ?? 0), blogAuto: !!data.data?.blog_auto_enabled, blogMaxDay: Number(data.data?.blog_max_per_day ?? 2) });
    }).catch(() => {});
  }, [user]);

  // Log: caricamento paginato (25 alla volta, dal più recente) al cambio di sotto-sezione
  useEffect(() => {
    if (tab !== "logs" || !user?.is_admin) return;
    setLogSearch("");
    if (logTab === "sync") loadSyncLogs(0);
    else if (logTab === "client") loadClientErrors(0);
    else loadAppLogs(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, logTab, user]);

  // Guardia "modifiche non salvate": chiede conferma (dialogo grande) prima di cambiare
  // tab, seguire link interni o chiudere/aggiornare la pagina.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (hasUnsaved()) { e.preventDefault(); e.returnValue = ""; }
    };
    const onClick = (e) => {
      if (!hasUnsaved()) return;
      const a = e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!href.startsWith("/")) return;
      e.preventDefault();
      e.stopPropagation();
      if (href.startsWith("/admin")) {
        const p = new URLSearchParams(href.split("?")[1] || "").get("tab");
        setUnsavedNav({ tab: p || "overview" });
      } else {
        setUnsavedNav({ href });
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, []);

  const goTab = (k) => {
    if (hasUnsaved()) setUnsavedNav({ tab: k });
    else setTab(k);
  };

  const resolveUnsaved = async (mode) => {
    const nav = unsavedNav;
    if (mode === "cancel" || !nav) { setUnsavedNav(null); return; }
    try {
      if (mode === "save") await saveUnsaved();
      else discardUnsaved();
    } catch {
      toast.error(t("misc.error"));
      setUnsavedNav(null);
      return;
    }
    setUnsavedNav(null);
    if (nav.tab) setTab(nav.tab);
    else if (nav.href) navigate(nav.href);
  };

  // Il tab Impostazioni registra il proprio stato "modificato" mentre è visibile
  useEffect(() => {
    if (tab !== "settings" || !settingsBase || !settingsBase.company) return;
    const h = {
      dirty: () => copyEmail !== settingsBase.email_global_copy ||
        digestTimes !== settingsBase.digest_times ||
        emailLoops !== settingsBase.emailLoops ||
        emailBlockTests !== settingsBase.emailBlockTests ||
        twofaSms !== settingsBase.twofaSms ||
        twofaEmail !== settingsBase.twofaEmail ||
        twofaAdminReq !== settingsBase.twofaAdminReq ||
        Number(quietFrom) !== settingsBase.quietFrom ||
        Number(quietTo) !== settingsBase.quietTo ||
        Number(markupPct || 0) !== settingsBase.markupPct ||
        blogAuto !== settingsBase.blogAuto ||
        Number(blogMaxDay || 2) !== settingsBase.blogMaxDay ||
        JSON.stringify(companyForm) !== JSON.stringify(settingsBase.company),
      save: saveSettings,
      discard: () => {
        setCopyEmail(settingsBase.email_global_copy);
        setDigestTimes(settingsBase.digest_times);
        setEmailLoops(settingsBase.emailLoops);
        setEmailBlockTests(settingsBase.emailBlockTests);
        setTwofaSms(settingsBase.twofaSms);
        setTwofaEmail(settingsBase.twofaEmail);
        setTwofaAdminReq(settingsBase.twofaAdminReq);
        setQuietFrom(settingsBase.quietFrom);
        setQuietTo(settingsBase.quietTo);
        setMarkupPct(String(settingsBase.markupPct));
        setBlogAuto(settingsBase.blogAuto);
        setBlogMaxDay(String(settingsBase.blogMaxDay));
        setCompanyForm(settingsBase.company);
      },
    };
    registerUnsaved(h);
    return () => unregisterUnsaved(h);
  });

  // Auto-salvataggio impostazioni: 2 secondi dopo l'ultima modifica salva da solo
  useEffect(() => {
    if (tab !== "settings" || !settingsBase || !settingsBase.company) return undefined;
    const isDirty = copyEmail !== settingsBase.email_global_copy ||
      digestTimes !== settingsBase.digest_times ||
      emailLoops !== settingsBase.emailLoops ||
      emailBlockTests !== settingsBase.emailBlockTests ||
      twofaSms !== settingsBase.twofaSms ||
      twofaEmail !== settingsBase.twofaEmail ||
      twofaAdminReq !== settingsBase.twofaAdminReq ||
      Number(quietFrom) !== settingsBase.quietFrom ||
      Number(quietTo) !== settingsBase.quietTo ||
      Number(markupPct || 0) !== settingsBase.markupPct ||
      blogAuto !== settingsBase.blogAuto ||
      Number(blogMaxDay || 2) !== settingsBase.blogMaxDay ||
      JSON.stringify(companyForm) !== JSON.stringify(settingsBase.company);
    if (!isDirty) return undefined;
    const tm = setTimeout(() => { saveSettings().catch(() => {}); }, 2000);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, copyEmail, digestTimes, emailLoops, emailBlockTests, twofaSms, twofaEmail, twofaAdminReq, quietFrom, quietTo, markupPct, blogAuto, blogMaxDay, companyForm, settingsBase]);

  const replyToTicket = async (tk) => {
    const reply = (replyDrafts[tk.ticket_id] || "").trim();
    if (reply.length < 2) return;
    setTicketBusy(tk.ticket_id);
    try {
      await api.post(`/admin/tickets/${tk.ticket_id}/reply`, { reply });
      toast.success(t("ticket.replySent"));
      setTickets((ts) => ts.map((x) => x.ticket_id === tk.ticket_id ? { ...x, admin_reply: reply, status: "answered" } : x));
      setReplyDrafts((d) => ({ ...d, [tk.ticket_id]: "" }));
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setTicketBusy(null);
    }
  };

  const closeTicket = async (tk) => {
    setTicketBusy(tk.ticket_id);
    try {
      await api.post(`/admin/tickets/${tk.ticket_id}/close`);
      setTickets((ts) => ts.map((x) => x.ticket_id === tk.ticket_id ? { ...x, status: "closed" } : x));
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setTicketBusy(null);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-now");
      setSyncLogs((ls) => [data.data, ...(ls || [])]);
      if (data.data.status === "success") toast.success(t("admin.syncDone"));
      else toast.error(t("admin.syncFailed"));
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get(`/admin/stats/sales?days=${salesDays}`).then(({ data }) => setSales(data.data || [])).catch(() => setSales([]));
  }, [user, salesDays]);

  const openDetail = async (userId) => {
    try {
      const { data } = await api.get(`/admin/customers/${userId}`);
      setDetail(data.data);
    } catch {
      toast.error(t("misc.error"));
    }
  };

  if (user === undefined || (user?.is_admin && !overview)) {
    return <div className="py-32 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }
  if (user && !user.is_admin) {
    return <div data-testid="admin-forbidden" className="py-32 text-center text-slate-500">{t("admin.forbidden")}</div>;
  }
  if (!user) return null;

  const downloadCsv = async (kind) => {
    try {
      const res = await api.get(`/admin/export/${kind}.csv?period=${period}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `simamma-${kind}-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("misc.error"));
    }
  };

  const exportCustomers = async () => {
    try {
      const res = await api.get("/admin/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `simamma_clienti_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("misc.error"));
    }
  };

  const openDoc = async (u) => {
    try {
      const res = await api.get(`/admin/customers/${u.user_id}/document`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error(t("misc.error"));
    }
  };

  const requestDoc = async (u) => {
    // "Richiedi identificazione cliente": invia l'invito KYC via email (percorso con QR/camera guidata)
    try {
      await api.post("/admin/identity/invite", { user_id: u.user_id, channel: "email" });
      toast.success(t("admin.docRequested"));
      setCustomers((cs) => cs.map((c) => c.user_id === u.user_id ? { ...c, kyc: { ...(c.kyc || {}), document_requested: true, document_verified: false } } : c));
    } catch (e) {
      toast.error(e.response?.data?.detail === "identity_invite_unavailable" ? t("admin.identityAlready") : t("misc.error"));
    }
  };

  const verifyEmail = async (u) => {
    try {
      await api.post(`/admin/customers/${u.user_id}/verify-email`);
      toast.success(t("admin.saved"));
      setCustomers((cs) => cs.map((c) => c.user_id === u.user_id ? { ...c, email_verified: true } : c));
    } catch {
      toast.error(t("misc.error"));
    }
  };

  const verifyPhone = async (u) => {
    try {
      await api.post(`/admin/customers/${u.user_id}/verify-phone`);
      toast.success(t("admin.saved"));
      setCustomers((cs) => cs.map((c) => c.user_id === u.user_id ? { ...c, phone_verified: true } : c));
    } catch (e) {
      toast.error(e.response?.data?.detail === "no_phone_number" ? t("admin.noPhone") : t("misc.error"));
    }
  };

  const verifyDoc = async (u) => {
    try {
      await api.post(`/admin/customers/${u.user_id}/verify-document`);
      toast.success(t("admin.docVerified"));
      setCustomers((cs) => cs.map((c) => c.user_id === u.user_id ? { ...c, kyc: { ...(c.kyc || {}), document_verified: true } } : c));
      setDetail((d) => (d && d.user_id === u.user_id ? { ...d, kyc: { ...(d.kyc || {}), document_verified: true } } : d));
    } catch {
      toast.error(t("misc.error"));
    }
  };

  const TAB_LABELS = { overview: t("admin.overview"), customers: t("admin.customers"), directory: t("admin.directory"), orders: t("admin.orders"), transactions: t("admin.transactions"), esims: "eSIM", credit: t("admin.credit"), failures: t("admin.failures"), invoices: t("admin.invoices"), margins: t("admin.margins"), referral: t("admin.referral"), dealers: t("admin.dealers"), identity: "Identità", blog: "Blog", marketing: "Marketing", tickets: t("admin.tickets"), logs: t("admin.logs"), integrations: t("admin.integrations"), security: t("admin.security"), settings: t("admin.settings"), ai: "AI & Costi" };

  // Menu a due livelli: 4 categorie grandi sempre in alto, sottocategorie sotto.
  const GROUPS = [
    { key: "overview", label: t("admin.grp.overview"), tabs: ["overview"] },
    { key: "sales", label: t("admin.grp.sales"), tabs: ["orders", "customers", "identity", "esims", "directory", "transactions", "referral", "blog", "marketing"] },
    { key: "administration", label: t("admin.grp.admin"), tabs: ["invoices", "credit", "failures", "margins"] },
    { key: "technical", label: t("admin.grp.tech"), tabs: ["tickets", "logs", "integrations", "security", "dealers", "ai", "settings"] },
  ];
  const activeGroup = GROUPS.find((g) => g.tabs.includes(tab)) || GROUPS[0];

  const q = searchQ.trim().toLowerCase();
  const isTrusted = (c) => c.kyc?.document_verified && c.phone_verified;
  const custFilterFn = (c) => {
    if (custFilter === "verified") return isTrusted(c);
    if (custFilter === "no_kyc") return !c.kyc?.first_name;
    if (custFilter === "never") return !c.last_login_at;
    if (custFilter === "buyers") return (c.orders_count || 0) > 0;
    if (custFilter === "business") return c.kyc?.account_type === "business";
    if (custFilter === "private") return (c.kyc?.account_type || "private") === "private";
    return true;
  };
  const filteredCustomers = (customers || [])
    .filter(custFilterFn)
    .filter((c) =>
      !q || [c.email, c.name, c.kyc?.first_name, c.kyc?.last_name, c.kyc?.tax_id].some((v) => (v || "").toLowerCase().includes(q)));
  const filteredOrders = (orders || []).filter((o) =>
    !q || [o.user_email, o.code, o.package_title, o.destination].some((v) => (v || "").toLowerCase().includes(q)));

  const th = "text-left font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-5 py-3";
  const td = "px-5 py-4 text-sm text-slate-700 align-top";

  return (
    <div data-testid="admin-dashboard" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-16">
      <Reveal>
        <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-3">SIMAMMA · ADMIN</div>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900">{t("admin.title")}</h1>
      </Reveal>

      <div data-testid="admin-tabbar" className="sticky top-16 z-40 -mx-4 sm:-mx-6 lg:-mx-12 mt-6 px-4 sm:px-6 lg:px-12 py-3 bg-[#FBF9F5]/90 backdrop-blur-xl border-b border-slate-200/80">
        {/* Categorie grandi, sempre in alto */}
        <div className="flex gap-2 flex-wrap">
          {GROUPS.map((g) => (
            <button key={g.key} data-testid={`admin-grp-${g.key}`} onClick={() => goTab(g.tabs[0])}
              className={`rounded-full px-6 py-3 font-display font-bold text-sm transition-colors ${
                activeGroup.key === g.key ? "bg-[#1E293B] text-white shadow-md" : "bg-white border border-slate-200 text-slate-700 hover:border-[#FF6B35] hover:text-[#FF6B35]"
              }`}>
              {g.label}
            </button>
          ))}
        </div>
        {/* Sottocategorie della categoria attiva */}
        {activeGroup.tabs.length > 1 && (
          <div className="mt-2.5 flex gap-2 flex-wrap">
            {activeGroup.tabs.map((k) => (
              <button key={k} data-testid={`admin-tab-${k}`} onClick={() => goTab(k)}
                className={`rounded-full px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
                  tab === k ? "bg-[#FF6B35] text-white shadow-md shadow-orange-200" : "bg-white border border-slate-200 text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35]"
                }`}>
                {TAB_LABELS[k] || k}
              </button>
            ))}
          </div>
        )}
      </div>

      {creditAlert?.alert_active && (
        <div data-testid="credit-alert-banner" className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-red-700 font-medium">⚠ {creditAlert.alert_reasons?.join(" · ") || t("credit.banner")}</div>
          <div className="flex gap-2">
            <a href="https://partners.airalo.com/" target="_blank" rel="noreferrer" data-testid="credit-banner-recharge"
              className="rounded-full bg-red-600 text-white px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-red-700 transition-colors">
              {t("credit.recharge")}
            </a>
            <button data-testid="credit-banner-open" onClick={() => goTab("credit")}
              className="rounded-full border border-red-300 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-red-600 hover:bg-red-100 transition-colors">
              {t("admin.credit")}
            </button>
          </div>
        </div>
      )}

      {tab === "overview" && (
        <>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label={t("admin.customers")} value={overview?.users ?? "—"} />
            <Stat label={t("admin.orders")} value={overview?.orders ?? "—"} />
            <Stat label={t("admin.paid")} value={overview?.paid ?? "—"} />
            <Stat label={`${t("admin.pending")} / ${t("admin.failed")}`} value={`${overview?.pending ?? "—"} / ${overview?.failed ?? "—"}`} />
            <Stat label={t("admin.airaloBalance")} value={airaloBalance?.amount_eur != null ? `€ ${Number(airaloBalance.amount_eur).toFixed(2)}` : "—"} />
            <Stat label={t("admin.walletLiability")} value={overview ? `€ ${Number(overview.wallet_liability || 0).toFixed(2)}` : "—"} />
            <Stat label={t("admin.coverage")} value={creditAlert?.coverage_pct != null ? `${creditAlert.coverage_pct}%` : "—"} />
          </div>

          <div className="mt-10 rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-bold text-xl text-slate-900">{t("admin.sales")}</h2>
              <div className="flex gap-2">
                {[7, 30, 90].map((d) => (
                  <button key={d} data-testid={`admin-sales-${d}d`} onClick={() => setSalesDays(d)}
                    className={`rounded-full px-3.5 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
                      salesDays === d ? "bg-[#1E293B] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"
                    }`}>
                    {d}D
                  </button>
                ))}
              </div>
            </div>
            <div data-testid="admin-sales-chart" className="h-56">
              {sales ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sales} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF6B35" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#FF6B35" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(d) => d.slice(5)} tickLine={false} axisLine={false} minTickGap={28} />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 14, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(v) => [fmt(v), t("admin.sales")]} />
                    <Area type="monotone" dataKey="total" stroke="#FF6B35" strokeWidth={2.5} fill="url(#salesFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">{t("misc.loading")}</div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
        {["customers", "orders"].includes(tab) ? (
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            <div className="flex max-w-sm flex-1 rounded-full border border-slate-200 bg-white shadow-sm overflow-hidden focus-within:border-[#FF6B35] transition-colors">
              <span className="pl-4 flex items-center text-slate-400"><Search className="w-4 h-4" /></span>
              <input data-testid="admin-search-input" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                placeholder={t("admin.searchPh")} className="flex-1 px-3 py-2.5 bg-transparent outline-none text-sm" />
            </div>
            {tab === "customers" && (
              <div className="flex gap-2 flex-wrap items-center">
                {[["all", t("admin.fAll")], ["verified", t("admin.onlyVerified")], ["no_kyc", t("admin.fNoKyc")], ["never", t("admin.fNever")], ["buyers", t("admin.fBuyers")], ["business", t("admin.fBusiness")], ["private", t("admin.fPrivate")]].map(([k, label]) => (
                  <button key={k} data-testid={k === "verified" ? "admin-only-verified" : `admin-filter-${k}`}
                    onClick={() => setCustFilter(k)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
                      custFilter === k ? (k === "verified" ? "bg-emerald-600 text-white" : "bg-[#1E293B] text-white") : "bg-white border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"
                    }`}>
                    {k === "verified" && <ShieldCheck className="w-3.5 h-3.5" />} {label}
                  </button>
                ))}
                <button data-testid="admin-customers-export" onClick={exportCustomers}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                  <FileDown className="w-3.5 h-3.5" /> {t("admin.exportCustomers")}
                </button>
              </div>
            )}
          </div>
        ) : <div />}
        <div className="flex items-center gap-2 flex-wrap">
          {[["day", t("admin.periodDay")], ["month", t("admin.periodMonth")], ["quarter", t("admin.periodQuarter")], ["semester", t("admin.periodSemester")], ["year", t("admin.periodYear")]].map(([k, label]) => (
            <button key={k} data-testid={`admin-period-${k}`} onClick={() => setPeriod(k)}
              className={`rounded-full px-3.5 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
                period === k ? "bg-[#1E293B] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"
              }`}>
              {label}
            </button>
          ))}
          <button data-testid="admin-export-customers" onClick={() => downloadCsv("customers")}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] text-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#0F766E] transition-colors">
            <Download className="w-3 h-3" /> {t("admin.csvCustomers")}
          </button>
          <button data-testid="admin-export-orders" onClick={() => downloadCsv("orders")}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] text-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#0F766E] transition-colors">
            <Download className="w-3 h-3" /> {t("admin.csvOrders")}
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm overflow-x-auto">
        {tab === "overview" && (
          <div data-testid="admin-overview-panel" className="p-5 sm:p-7 min-w-[760px]">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Stat label={t("admin.revenue")} value={overview ? `$${(overview.revenue || 0).toFixed(2)}` : "—"} />
              <Stat label={t("admin.revenue30")} value={overview ? `$${(overview.revenue_30d || 0).toFixed(2)}` : "—"} />
              <Stat label={t("admin.orders30")} value={overview?.orders_30d ?? "—"} />
              <Stat label={t("admin.avgOrder")} value={overview ? `$${(overview.avg_order || 0).toFixed(2)}` : "—"} />
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              <button data-testid="admin-chip-never" onClick={() => { setCustFilter("never"); setTab("customers"); }}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                {t("admin.fNever")}: {overview?.never_logged ?? "—"}
              </button>
              <button data-testid="admin-chip-tickets" onClick={() => setTab("tickets")}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                {t("admin.openTickets")}: {overview?.tickets_open ?? "—"}
              </button>
            </div>

            <div data-testid="admin-winback-stats" className="mb-8 rounded-2xl border border-slate-200/80 p-5">
              <h3 className="font-display font-bold text-base text-slate-900 mb-4">{t("admin.winback")}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                <Stat label={t("admin.wbEmails")} value={overview?.winback?.emails_sent ?? "—"} />
                <Stat label={t("admin.wbReminders")} value={overview?.winback?.reminders ?? "—"} />
                <Stat label={t("admin.wbActive")} value={overview?.winback?.active_offers ?? "—"} />
                <Stat label={t("admin.wbUsed")} value={overview?.winback?.discounts_used ?? "—"} />
                <Stat label={t("admin.wbExpired")} value={overview?.winback?.expired_notices ?? "—"} />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-display font-bold text-lg mb-4">{t("admin.topDest")}</h3>
                <div data-testid="admin-top-dest" className="space-y-3">
                  {(overview?.top_destinations || []).map((d, i) => {
                    const max = overview.top_destinations[0]?.revenue || 1;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="w-40 truncate text-sm font-medium text-slate-700">{d.destination}</span>
                        <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-[#FF6B35]" style={{ width: `${Math.max(6, (d.revenue / max) * 100)}%` }} />
                        </div>
                        <span className="font-mono-t text-xs font-bold text-slate-600 w-20 text-right">${d.revenue.toFixed(2)}</span>
                      </div>
                    );
                  })}
                  {overview && (overview.top_destinations || []).length === 0 && (
                    <p className="text-sm text-slate-400">{t("admin.noLogs")}</p>
                  )}
                </div>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg mb-4">{t("admin.recentLogins")}</h3>
                <div data-testid="admin-recent-logins" className="space-y-1.5">
                  {(overview?.recent_logins || []).map((l, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-2.5 hover:bg-[#FAF5EE] transition-colors">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                      <span className="text-sm text-slate-700 flex-1 min-w-[160px] truncate">{l.message}</span>
                      <span className="font-mono-t text-[10px] text-slate-400 shrink-0">{new Date(l.ts).toLocaleString()}</span>
                    </div>
                  ))}
                  {overview && (overview.recent_logins || []).length === 0 && (
                    <p className="text-sm text-slate-400">{t("admin.noLogs")}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "customers" && (
          <table data-testid="admin-customers-table" className="w-full min-w-[760px]">
            <thead><tr className="border-b border-slate-100">
              <th className={th}>Email</th><th className={th}>{t("kyc.firstName")} / {t("kyc.lastName")}</th><th className={th}>{t("kyc.taxId")}</th>
              <th className={th}>{t("admin.orders")}</th><th className={th}>{t("admin.spent")}</th><th className={th}>{t("admin.lastLogin")}</th><th className={th}>SIM</th><th className={th}>{t("admin.downloadDoc")}</th><th className={th}>{t("admin.colVerifications")}</th>
            </tr></thead>
            <tbody>
              {filteredCustomers.map((c) => (
                <tr key={c.user_id} data-testid={`admin-customer-${c.user_id}`} onClick={() => openDetail(c.user_id)} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors cursor-pointer">
                  <td className={td}>
                    <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                      {c.name}
                      {c.kyc?.document_verified && c.phone_verified && (
                        <span data-testid={`admin-trust-${c.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-600 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                          <ShieldCheck className="w-3 h-3" /> {t("admin.trusted")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">{c.email}</div>
                  </td>
                  <td className={td}>{c.kyc?.first_name ? `${c.kyc.first_name} ${c.kyc.last_name}` : <span className="text-slate-300">{t("admin.kycMissing")}</span>}</td>
                  <td className={`${td} font-mono-t text-xs`}>{c.kyc?.tax_id || "—"}</td>
                  <td className={td}>{c.orders_count}</td>
                  <td className={`${td} font-mono-t font-bold`}>{fmt(c.total_spent)}</td>
                  <td className={`${td} whitespace-nowrap`}>
                    {c.last_login_at ? (
                      <span className="font-mono-t text-xs text-slate-600">{new Date(c.last_login_at).toLocaleString()}</span>
                    ) : (
                      <span data-testid={`admin-never-${c.user_id}`} className="rounded-full bg-slate-100 text-slate-400 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">{t("admin.never")}</span>
                    )}
                  </td>
                  <td className={td}>
                    <button data-testid={`admin-customer-sims-${c.user_id}`}
                      onClick={(e) => { e.stopPropagation(); setEsimQuery(c.email); goTab("esims"); }}
                      className="rounded-full border border-slate-200 px-3.5 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                      SIM →
                    </button>
                  </td>
                  <td className={td}>
                    {c.kyc?.document_uploaded ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <button data-testid={`admin-doc-${c.user_id}`} onClick={(e) => { e.stopPropagation(); openDoc(c); }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#E6F7F5] text-[#0D9488] px-3 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#0D9488] hover:text-white transition-colors">
                          <FileText className="w-3 h-3" /> {t("admin.downloadDoc")}
                        </button>
                        {c.kyc?.document_verified ? (
                          <span data-testid={`admin-doc-verified-${c.user_id}`} className="rounded-full bg-emerald-50 text-emerald-600 px-3 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest">{t("admin.docVerified")}</span>
                        ) : (
                          <button data-testid={`admin-verify-doc-${c.user_id}`} onClick={(e) => { e.stopPropagation(); verifyDoc(c); }}
                            className="rounded-full border border-emerald-300 text-emerald-600 px-3 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors">
                            {t("admin.verifyDoc")}
                          </button>
                        )}
                      </div>
                    ) : c.kyc?.document_requested ? (
                      <span className="font-mono-t text-[10px] uppercase tracking-widest text-amber-500">{t("admin.docRequested")}</span>
                    ) : (
                      <button data-testid={`admin-request-doc-${c.user_id}`} onClick={(e) => { e.stopPropagation(); requestDoc(c); }}
                        className="rounded-full border border-slate-200 px-3 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        {t("admin.requestDoc")}
                      </button>
                    )}
                  </td>
                  <td className={td}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {c.email_verified ? (
                        <span data-testid={`admin-email-ok-${c.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-600 px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                          <CheckCircle2 className="w-3 h-3" /> Email
                        </span>
                      ) : (
                        <button data-testid={`admin-verify-email-${c.user_id}`} onClick={(e) => { e.stopPropagation(); verifyEmail(c); }}
                          className="rounded-full border border-slate-200 px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                          {t("admin.verifyEmail")}
                        </button>
                      )}
                      {c.phone_verified ? (
                        <span data-testid={`admin-phone-ok-${c.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-600 px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                          <CheckCircle2 className="w-3 h-3" /> Cell
                        </span>
                      ) : (
                        <button data-testid={`admin-verify-phone-${c.user_id}`} onClick={(e) => { e.stopPropagation(); verifyPhone(c); }}
                          className="rounded-full border border-slate-200 px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                          {t("admin.verifyPhone")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === "orders" && (
          <table data-testid="admin-orders-table" className="w-full min-w-[760px]">
            <thead><tr className="border-b border-slate-100">
              <th className={th}>{t("acc.order")}</th><th className={th}>Email</th><th className={th}>{t("dest.plans")}</th>
              <th className={th}>{t("acc.status")}</th><th className={th}>{t("admin.spent")}</th><th className={th}></th>
            </tr></thead>
            <tbody>
              {filteredOrders.map((o) => (
                <tr key={o.order_id} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className={`${td} font-mono-t text-xs`}>
                    {o.code || o.order_id}
                    <div className="text-[10px] text-slate-300">{new Date(o.created_at).toLocaleString()} {o.mock && "· DEMO"}</div>
                  </td>
                  <td className={td}>{o.user_email}</td>
                  <td className={td}>
                    <div className="font-medium text-slate-900">{o.package_title}</div>
                    <div className="text-xs text-slate-400">{o.destination} · {o.kind}</div>
                  </td>
                  <td className={td}><StatusPill status={o.status} /></td>
                  <td className={`${td} font-mono-t font-bold`}>{o.price} {o.currency}</td>
                  <td className={td}>
                    {o.status === "pending_identity" && (
                      <button data-testid={`admin-release-${o.order_id}`}
                        onClick={async () => {
                          try {
                            await api.post(`/admin/orders/${o.order_id}/release-identity`);
                            toast.success(t("admin.released"));
                          } catch (err) {
                            toast.error(err.response?.data?.detail || t("misc.error"));
                          }
                          api.get("/admin/orders").then(({ data }) => setOrders(data.data || [])).catch(() => {});
                        }}
                        className="rounded-full border border-violet-300 px-4 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-violet-600 hover:bg-violet-50 transition-colors">
                        {t("admin.releaseIdentity")}
                      </button>
                    )}
                    {(o.status === "completed") && o.kind !== "admin_topup" && (
                      <button data-testid={`admin-refund-${o.order_id}`} onClick={() => setRefundOrder(o)}
                        className="rounded-full border border-slate-200 px-4 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-red-400 hover:text-red-500 transition-colors">
                        {t("admin.refund")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === "transactions" && (
          <table data-testid="admin-transactions-table" className="w-full min-w-[760px]">
            <thead><tr className="border-b border-slate-100">
              <th className={th}>Session</th><th className={th}>Email</th><th className={th}>{t("dest.plans")}</th>
              <th className={th}>Stripe</th><th className={th}>{t("admin.spent")}</th>
            </tr></thead>
            <tbody>
              {(txs || []).map((x) => (
                <tr key={x.session_id} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className={`${td} font-mono-t text-xs`}>
                    {x.session_id.slice(0, 24)}…
                    <div className="text-[10px] text-slate-300">{new Date(x.created_at).toLocaleString()}</div>
                  </td>
                  <td className={td}>{x.user_email}</td>
                  <td className={td}>{x.package_title}</td>
                  <td className={td}><StatusPill status={x.payment_status} /></td>
                  <td className={`${td} font-mono-t font-bold`}>{x.amount} {(x.currency || "usd").toUpperCase()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === "esims" && (
          <AdminSims query={esimQuery} onQueryChange={setEsimQuery} onTopup={(s) => setTopupSim(s)} />
        )}
        {tab === "tickets" && (
          <div data-testid="admin-tickets-panel" className="p-5 sm:p-7 min-w-[760px] space-y-3">
            {(tickets || []).map((tk) => (
              <details key={tk.ticket_id} data-testid={`admin-ticket-${tk.ticket_id}`} className="rounded-2xl border border-slate-200/80 px-5 py-4">
                <summary className="flex flex-wrap items-center gap-3 cursor-pointer list-none">
                  <span className={`rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${
                    tk.status === "open" ? "bg-orange-50 text-[#E8551F]" : tk.status === "answered" ? "bg-sky-50 text-sky-600" : "bg-slate-100 text-slate-400"
                  }`}>{t(`ticket.${tk.status}`)}</span>
                  <span className="font-medium text-slate-900">{tk.subject}</span>
                  <span className="text-xs text-slate-400">{tk.email}</span>
                  <span className="ml-auto font-mono-t text-[10px] text-slate-400">{new Date(tk.created_at).toLocaleString()}</span>
                </summary>
                <div className="mt-4 border-t border-slate-100 pt-4 space-y-4">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap rounded-xl bg-[#FAF5EE] px-4 py-3">{tk.message}</p>
                  {tk.admin_reply && (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap rounded-xl border-l-2 border-[#FF6B35] bg-orange-50/50 px-4 py-3">
                      <span className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-[#FF6B35] block mb-1">SIMAMMA</span>
                      {tk.admin_reply}
                    </p>
                  )}
                  {tk.status !== "closed" && (
                    <div className="flex flex-col gap-3">
                      <textarea data-testid={`admin-ticket-reply-${tk.ticket_id}`} rows={3} maxLength={2000}
                        value={replyDrafts[tk.ticket_id] || ""}
                        onChange={(e) => setReplyDrafts((d) => ({ ...d, [tk.ticket_id]: e.target.value }))}
                        placeholder={t("ticket.replyPh")}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#FF6B35] transition-colors resize-none" />
                      <div className="flex gap-2">
                        <button data-testid={`admin-ticket-send-${tk.ticket_id}`} onClick={() => replyToTicket(tk)}
                          disabled={ticketBusy === tk.ticket_id}
                          className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] disabled:opacity-60 transition-colors">
                          {ticketBusy === tk.ticket_id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                          {t("ticket.sendReply")}
                        </button>
                        <button data-testid={`admin-ticket-close-${tk.ticket_id}`} onClick={() => closeTicket(tk)}
                          disabled={ticketBusy === tk.ticket_id}
                          className="rounded-full border border-slate-200 px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-slate-900 hover:text-slate-900 transition-colors">
                          {t("ticket.close")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </details>
            ))}
            {tickets && tickets.length === 0 && (
              <p data-testid="admin-no-tickets" className="py-10 text-left text-sm text-slate-400">{t("admin.noTickets")}</p>
            )}
          </div>
        )}

        {tab === "logs" && (
          <div data-testid="admin-logs-panel" className="p-5 sm:p-7 min-w-[760px]">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex gap-2">
                {[["sync", t("admin.logSync")], ["events", t("admin.logEvents")], ["errors", t("admin.logErrors")], ["health", t("admin.logHealth")], ["client", t("admin.logClient")]].map(([k, label]) => (
                  <button key={k} data-testid={`admin-logtab-${k}`} onClick={() => setLogTab(k)}
                    className={`rounded-full px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
                      logTab === k ? "bg-[#1E293B] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              <button data-testid="admin-sync-now" onClick={syncNow} disabled={syncing}
                className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] disabled:opacity-60 transition-colors">
                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {syncing ? t("admin.syncing") : t("admin.syncNow")}
              </button>
            </div>

            {logTab !== "sync" && (
              <div className="flex max-w-sm rounded-full border border-slate-200 bg-white shadow-sm overflow-hidden focus-within:border-[#FF6B35] transition-colors mb-4">
                <span className="pl-4 flex items-center text-slate-400"><Search className="w-4 h-4" /></span>
                <input data-testid="admin-log-search" value={logSearch} onChange={(e) => setLogSearch(e.target.value)}
                  placeholder={t("admin.searchLogs")} className="flex-1 px-3 py-2.5 bg-transparent outline-none text-sm" />
              </div>
            )}

            {logTab === "sync" && (
              <div className="space-y-3">
                {(syncLogs || []).map((l) => (
                  <details key={l.sync_id} data-testid={`admin-sync-${l.sync_id}`} className="rounded-2xl border border-slate-200/80 px-5 py-4">
                    <summary className="flex flex-wrap items-center gap-3 cursor-pointer list-none">
                      <StatusPill status={l.status === "success" ? "completed" : "failed"} />
                      <span className="font-mono-t text-xs text-slate-500">{new Date(l.ts).toLocaleString()}</span>
                      <span className="font-mono-t text-[10px] uppercase tracking-widest text-slate-400">
                        {l.source === "manual" ? t("admin.manual") : t("admin.auto")}
                      </span>
                      {l.status === "success" ? (
                        <span className="text-sm text-slate-600">
                          {l.counts.total} · <span className="text-emerald-600 font-bold">+{l.counts.added}</span>{" "}
                          <span className="text-red-500 font-bold">−{l.counts.removed}</span>{" "}
                          <span className="text-amber-600 font-bold">~{l.counts.price}</span>
                          {l.initial && <span className="text-slate-400"> · {t("admin.initial")}</span>}
                        </span>
                      ) : (
                        <span className="text-sm text-red-500">{l.error}</span>
                      )}
                      <span className="ml-auto font-mono-t text-[10px] text-slate-300">{((l.duration_ms || 0) / 1000).toFixed(1)}s</span>
                      {l.status === "success" && l.prev_snapshot_id && (
                        <button data-testid={`admin-rollback-${l.sync_id}`}
                          onClick={(e) => { e.preventDefault(); rollbackSync(l.sync_id); }}
                          className="rounded-full border border-slate-200 px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                          {t("admin.rollback")}
                        </button>
                      )}
                    </summary>
                    {l.status === "success" && (
                      <div className="mt-3 border-t border-slate-100 pt-3">
                        {(!l.changes || l.changes.length === 0) ? (
                          <p className="text-sm text-slate-400">{t("admin.noChanges")}</p>
                        ) : (
                          <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {l.changes.map((c, i) => (
                              <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                                <span className={`w-6 text-center rounded-full font-mono-t text-[10px] font-bold ${
                                  c.kind === "added" ? "bg-emerald-50 text-emerald-600" : c.kind === "removed" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-600"
                                }`}>{c.kind === "added" ? "+" : c.kind === "removed" ? "−" : "~"}</span>
                                <span className="font-medium text-slate-800">{c.title}</span>
                                <span className="text-xs text-slate-400">{c.destination}</span>
                                {c.kind === "price" && (
                                  <span className="font-mono-t text-xs text-amber-600">{c.old_price} → {c.price} USD</span>
                                )}
                                {c.kind === "added" && (
                                  <span className="font-mono-t text-xs text-emerald-600">{c.price} USD</span>
                                )}
                              </div>
                            ))}
                            {l.changes_total > (l.changes || []).length && (
                              <p className="text-xs text-slate-400 pl-8">… +{l.changes_total - l.changes.length}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </details>
                ))}
                {syncLogs && syncLogs.length === 0 && (
                  <p data-testid="admin-no-sync-logs" className="py-8 text-left text-sm text-slate-400">{t("admin.noLogs")}</p>
                )}
                {logMore.sync && (
                  <div className="pt-2 text-center">
                    <button data-testid="admin-sync-more" onClick={() => loadSyncLogs((syncLogs || []).length)} disabled={logBusy}
                      className="rounded-full border border-slate-200 px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-50">
                      {logBusy ? "…" : t("admin.showMore")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {logTab === "client" && (() => {
              const qs = logSearch.trim().toLowerCase();
              const crows = (clientErrors || []).filter((l) => !qs || `${l.message} ${l.detail || ""} ${l.path || ""}`.toLowerCase().includes(qs));
              return (
              <div data-testid="admin-logs-client" className="space-y-1.5 max-h-[32rem] overflow-y-auto">
                {crows.map((l, i) => (
                  <div key={i} data-testid={`admin-client-err-${i}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-xl px-4 py-2.5 hover:bg-[#FAF5EE] transition-colors">
                    <span className="font-mono-t text-[10px] text-slate-400 shrink-0">{new Date(l.ts).toLocaleString()}</span>
                    <span className="rounded-full px-2 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest bg-violet-50 text-violet-600">{l.kind}</span>
                    <span className="text-sm text-slate-700 flex-1 min-w-[200px]">{l.message}</span>
                    <span className="font-mono-t text-[10px] text-slate-400">{l.path}</span>
                    {l.detail && <span className="font-mono-t text-[10px] text-slate-300 break-all">{l.detail}</span>}
                  </div>
                ))}
                {crows.length === 0 && (
                  <p className="py-8 text-left text-sm text-slate-400">{t("admin.noLogs")}</p>
                )}
                {logMore.client && (
                  <div className="pt-2 text-center">
                    <button data-testid="admin-client-more" onClick={() => loadClientErrors((clientErrors || []).length)} disabled={logBusy}
                      className="rounded-full border border-slate-200 px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-50">
                      {logBusy ? "…" : t("admin.showMore")}
                    </button>
                  </div>
                )}
              </div>
              );
            })()}

            {["events", "errors", "health"].includes(logTab) && (() => {
              const qs = logSearch.trim().toLowerCase();
              const rows = (appLogs || [])
                .filter((l) => logTab === "errors" ? l.category === "error" : logTab === "health" ? l.category === "health" : l.category !== "error")
                .filter((l) => !qs || `${l.message} ${typeof l.detail === "string" ? l.detail : JSON.stringify(l.detail || "")}`.toLowerCase().includes(qs));
              return (
                <div data-testid={`admin-logs-${logTab}`} className="space-y-1.5 max-h-[32rem] overflow-y-auto">
                  {rows.map((l) => (
                    <div key={l.log_id} data-testid={`admin-log-${l.log_id}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-xl px-4 py-2.5 hover:bg-[#FAF5EE] transition-colors">
                      <span className="font-mono-t text-[10px] text-slate-400 shrink-0">{new Date(l.ts).toLocaleString()}</span>
                      <span className={`rounded-full px-2 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${
                        l.category === "error" ? "bg-red-50 text-red-500" : l.category === "transaction" ? "bg-emerald-50 text-emerald-600" : l.category === "webhook" ? "bg-sky-50 text-sky-600" : "bg-slate-100 text-slate-500"
                      }`}>{l.category}</span>
                      <span className="text-sm text-slate-700 flex-1 min-w-[200px]">{l.message}</span>
                      {l.detail && (
                        <span className="font-mono-t text-[10px] text-slate-300 break-all">
                          {typeof l.detail === "string" ? l.detail : JSON.stringify(l.detail)}
                        </span>
                      )}
                    </div>
                  ))}
                  {appLogs && rows.length === 0 && (
                    <p className="py-8 text-left text-sm text-slate-400">{t("admin.noLogs")}</p>
                  )}
                  {logMore.app && (
                    <div className="pt-2 text-center">
                      <button data-testid="admin-applogs-more" onClick={() => loadAppLogs((appLogs || []).length)} disabled={logBusy}
                        className="rounded-full border border-slate-200 px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-50">
                        {logBusy ? "…" : t("admin.showMore")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {tab === "integrations" && <AdminIntegrations />}

        {tab === "credit" && <AdminCredit goTab={goTab} />}

        {tab === "failures" && <AdminCreditFailures />}

        {tab === "security" && <><AdminSecurity /><AdminConsents /></>}
        {tab === "ai" && <AdminAiUsage />}

        {tab === "invoices" && <AdminInvoices />}

        {tab === "margins" && <AdminMargins />}

        {tab === "referral" && <AdminReferrals />}

        {tab === "dealers" && <AdminDealers />}
        {tab === "directory" && <AdminDirectory />}

        {tab === "identity" && <AdminIdentity />}
        {tab === "marketing" && <><AdminMarketing /><AdminBonus /></>}

        {tab === "blog" && <AdminBlog />}

        {tab === "settings" && (
          <div data-testid="admin-settings-panel" className="p-5 sm:p-7 max-w-2xl">
            <h3 className="font-display font-bold text-lg text-slate-900">{t("admin.settings")}</h3>
            <p className="mt-1 mb-5 text-sm text-slate-500">{t("admin.globalCopyDesc")}</p>
            <label className="block font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
              {t("admin.globalCopy")}
            </label>
            <input data-testid="admin-settings-copy-input" type="email" value={copyEmail}
              onChange={(e) => setCopyEmail(e.target.value)} placeholder="nome@esempio.com"
              className="w-full rounded-full border border-slate-200 bg-transparent px-5 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />

            <h3 className="mt-8 mb-1 font-display font-bold text-lg text-slate-900">{t("admin.digestTimes")}</h3>
            <p className="mb-3 text-sm text-slate-500">{t("admin.digestTimesDesc")}</p>
            <input data-testid="admin-settings-digest-times" type="text" value={digestTimes}
              onChange={(e) => setDigestTimes(e.target.value)} placeholder="07:00, 19:00"
              className="w-full rounded-full border border-slate-200 bg-transparent px-5 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />

            <h3 className="mt-8 mb-1 font-display font-bold text-lg text-slate-900">{t("admin.catalogMarkup")}</h3>
            <p className="mb-3 text-sm text-slate-500">{t("admin.catalogMarkupDesc")}</p>
            <div className="flex items-center gap-3">
              <input data-testid="admin-settings-markup" type="number" min="0" max="300" step="0.5" value={markupPct}
                onChange={(e) => setMarkupPct(e.target.value)}
                className="w-28 rounded-full border border-slate-200 bg-transparent px-5 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
              <span className="font-mono-t text-xs font-bold text-slate-400">%</span>
            </div>

            <h3 className="mt-8 mb-1 font-display font-bold text-lg text-slate-900">{t("admin.blogAuto")}</h3>
            <p className="mb-3 text-sm text-slate-500">{t("admin.blogAutoDesc")}</p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3">
                <button type="button" data-testid="admin-settings-blog-auto" onClick={() => setBlogAuto(!blogAuto)}
                  className={`w-11 h-6 rounded-full p-0.5 transition-colors ${blogAuto ? "bg-[#FF6B35]" : "bg-slate-300"}`}>
                  <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${blogAuto ? "translate-x-5" : ""}`} />
                </button>
                <span className={`text-sm font-medium ${blogAuto ? "text-emerald-600" : "text-slate-400"}`}>
                  {blogAuto ? "ON" : "OFF"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input data-testid="admin-settings-blog-max" type="number" min="1" max="20" value={blogMaxDay}
                  onChange={(e) => setBlogMaxDay(e.target.value)}
                  className="w-20 rounded-full border border-slate-200 bg-transparent px-4 py-2 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
                <span className="text-sm text-slate-500">{t("admin.blogMaxDay")}</span>
              </div>
            </div>


            <h3 className="mt-8 mb-1 font-display font-bold text-lg text-slate-900">{t("admin.emailGate")}</h3>
            <p className="mb-3 text-sm text-slate-500">{t("admin.emailGateDesc")}</p>
            <div className="flex items-center gap-3 min-h-[44px]">
              <button type="button" data-testid="admin-settings-email-loops" onClick={() => setEmailLoops(!emailLoops)}
                className={`w-11 h-6 rounded-full p-0.5 transition-colors shrink-0 ${emailLoops ? "bg-[#FF6B35]" : "bg-slate-300"}`}>
                <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${emailLoops ? "translate-x-5" : ""}`} />
              </button>
              <span className="text-sm text-slate-700">{t("admin.emailLoops")}</span>
            </div>
            <div className="mt-3 flex items-center gap-3 min-h-[44px]">
              <button type="button" data-testid="admin-settings-block-test-users" onClick={() => setEmailBlockTests(!emailBlockTests)}
                className={`w-11 h-6 rounded-full p-0.5 transition-colors shrink-0 ${emailBlockTests ? "bg-[#FF6B35]" : "bg-slate-300"}`}>
                <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${emailBlockTests ? "translate-x-5" : ""}`} />
              </button>
              <div>
                <span className="text-sm text-slate-700">{t("admin.blockTestUsers")}</span>
                <p className="text-xs text-slate-400">{t("admin.blockTestUsersHint")}</p>
              </div>
            </div>
            <div className="mt-6 pt-5 border-t border-slate-200">
              <div className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-3">{t("admin.twofaSection")}</div>
              <div className="space-y-3">
                {[
                  { on: twofaAdminReq, set: setTwofaAdminReq, labelKey: "admin.twofaAdminMandatory", hintKey: "admin.twofaAdminMandatoryHint", testid: "admin-settings-twofa-admin" },
                  { on: twofaSms, set: setTwofaSms, labelKey: "admin.twofaSms", hintKey: "admin.twofaSmsHint", testid: "admin-settings-twofa-sms" },
                  { on: twofaEmail, set: setTwofaEmail, labelKey: "admin.twofaEmail", hintKey: "admin.twofaEmailHint", testid: "admin-settings-twofa-email" },
                ].map((row) => (
                  <div key={row.testid} className="flex items-center gap-3 min-h-[44px]">
                    <button type="button" data-testid={row.testid} onClick={() => row.set(!row.on)}
                      className={`w-11 h-6 rounded-full p-0.5 transition-colors shrink-0 ${row.on ? "bg-[#FF6B35]" : "bg-slate-300"}`}>
                      <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${row.on ? "translate-x-5" : ""}`} />
                    </button>
                    <div>
                      <span className="text-sm text-slate-700">{t(row.labelKey)}</span>
                      <p className="text-xs text-slate-400">{t(row.hintKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-slate-500">{t("admin.quietFrom")}</span>
              <input data-testid="admin-settings-quiet-from" type="number" min="0" max="23" value={quietFrom}
                onChange={(e) => setQuietFrom(e.target.value)}
                className="w-20 rounded-full border border-slate-200 bg-transparent px-4 py-2 text-sm outline-none focus:border-[#FF6B35]" />
              <span className="text-sm text-slate-500">{t("admin.quietTo")}</span>
              <input data-testid="admin-settings-quiet-to" type="number" min="0" max="23" value={quietTo}
                onChange={(e) => setQuietTo(e.target.value)}
                className="w-20 rounded-full border border-slate-200 bg-transparent px-4 py-2 text-sm outline-none focus:border-[#FF6B35]" />
            </div>
            <p className="mt-2 text-xs text-slate-400">{t("admin.quietNote")}</p>

            <div className="mt-6 rounded-2xl border border-slate-200 p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm text-slate-800">{t("admin.kycPackage")}</div>
                <p className="text-xs text-slate-500 mt-0.5">{t("admin.kycPackageDesc")}</p>
              </div>
              <button type="button" data-testid="admin-kyc-package-download" onClick={async () => {
                try {
                  const res = await api.get("/admin/kyc-package", { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = "kyc-package.zip"; a.click();
                  URL.revokeObjectURL(url);
                } catch { toast.error(t("misc.error")); }
              }}
                className="rounded-full bg-[#1E293B] text-white px-5 min-h-[44px] font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-slate-700 transition-colors shrink-0">
                ZIP
              </button>
            </div>

            <h3 className="mt-8 mb-1 font-display font-bold text-lg text-slate-900">{t("admin.company")}</h3>
            <p className="mb-5 text-sm text-slate-500">{t("admin.companyDesc")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[["name", "admin.coName"], ["vat", "admin.coVat"], ["address", "admin.coAddress"], ["zip", "admin.coZip"],
                ["city", "admin.coCity"], ["province", "admin.coProvince"], ["country", "admin.coCountry"],
                ["email", "admin.coEmail"], ["phone", "admin.coPhone"]].map(([field, key]) => (
                <div key={field}>
                  <label className="block font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">{t(key)}</label>
                  <input data-testid={`admin-settings-company-${field}`} type="text" value={companyForm[field] || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, [field]: e.target.value })}
                    className="w-full rounded-full border border-slate-200 bg-transparent px-5 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
                </div>
              ))}
            </div>
            <p data-testid="admin-settings-autosave" className="mt-6 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              {copyBusy && <Loader2 className="w-3 h-3 animate-spin text-[#FF6B35]" />}
              {copyBusy ? t("admin.autoSaving") : t("admin.autoNote")}
            </p>
          </div>
        )}

      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent data-testid="admin-customer-detail" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <div>
              <DialogHeader>
                <DialogTitle className="font-display">{t("admin.customerDetail")} — {detail.name}</DialogTitle>
              </DialogHeader>
              <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  ["Email", detail.email],
                  [t("kyc.firstName") + " / " + t("kyc.lastName"), detail.kyc?.first_name ? `${detail.kyc.first_name} ${detail.kyc.last_name}` : "—"],
                  [t("kyc.taxId"), detail.kyc?.tax_id || "—"],
                  [t("kyc.country"), detail.kyc?.country || "—"],
                  [t("acc.phoneTitle"), detail.phone ? `${detail.phone}${detail.phone_verified ? " ✓" : ""}` : "—"],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-2xl bg-[#FAF5EE] px-4 py-3">
                    <div className="font-mono-t text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">{l}</div>
                    <div className="mt-1 font-medium text-slate-900 break-all">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {detail.has_document ? (
                  <>
                    <button data-testid="admin-detail-doc" onClick={() => openDoc(detail)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#E6F7F5] text-[#0D9488] px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#0D9488] hover:text-white transition-colors">
                      <FileText className="w-3.5 h-3.5" /> {t("admin.downloadDoc")}
                    </button>
                    {detail.kyc?.document_verified ? (
                      <span className="rounded-full bg-emerald-50 text-emerald-600 px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest">{t("admin.docVerified")}</span>
                    ) : (
                      <button data-testid="admin-detail-verify-doc" onClick={() => verifyDoc(detail)}
                        className="rounded-full border border-emerald-300 text-emerald-600 px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors">
                        {t("admin.verifyDoc")}
                      </button>
                    )}
                  </>
                ) : (
                  <button data-testid="admin-detail-request-doc" onClick={() => requestDoc(detail)}
                    className="rounded-full border border-slate-200 px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                    {t("admin.requestDoc")}
                  </button>
                )}
              </div>
              <h3 className="mt-7 mb-3 font-display font-bold text-lg text-slate-900">{t("admin.orders")}</h3>
              <div className="space-y-2">
                {(detail.orders || []).map((o) => (
                  <div key={o.order_id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200/80 px-4 py-3 text-sm">
                    <div>
                      <div className="font-medium text-slate-900">{o.package_title}</div>
                      <div className="font-mono-t text-[10px] text-slate-400">{o.code || o.order_id} · {new Date(o.created_at).toLocaleDateString()} · {o.kind}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusPill status={o.status} />
                      <span className="font-mono-t font-bold text-slate-900">{o.price} {o.currency}</span>
                    </div>
                  </div>
                ))}
                {(!detail.orders || detail.orders.length === 0) && <p className="text-sm text-slate-400">{t("acc.none")}</p>}
              </div>
              <h3 className="mt-7 mb-3 font-display font-bold text-lg text-slate-900">eSIM</h3>
              <div className="space-y-2">
                {(detail.esims || []).map((s) => (
                  <div key={s.iccid} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200/80 px-4 py-3 text-sm">
                    <div>
                      <div className="font-medium text-slate-900">{s.package_title}</div>
                      <div className="font-mono-t text-[10px] text-slate-400">{s.iccid} {s.mock && "· DEMO"}</div>
                    </div>
                    <button data-testid={`admin-detail-topup-${s.iccid}`} onClick={() => setTopupSim(s)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#FF6B35] text-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors shrink-0">
                      <Zap className="w-3 h-3" /> {t("admin.topup")}
                    </button>
                  </div>
                ))}
                {(!detail.esims || detail.esims.length === 0) && <p className="text-sm text-slate-400">{t("acc.none")}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!unsavedNav} onOpenChange={() => resolveUnsaved("cancel")}>
        <DialogContent data-testid="admin-unsaved-dialog" className="sm:max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl sm:text-3xl font-black text-slate-900">{t("admin.unsavedTitle")}</DialogTitle>
          </DialogHeader>
          <p className="mt-3 font-display text-xl sm:text-2xl font-bold text-[#FF6B35]">{t("admin.unsavedAsk")}</p>
          <div className="mt-7 flex flex-col gap-3">
            <button data-testid="admin-unsaved-save" onClick={() => resolveUnsaved("save")}
              className="rounded-full bg-[#FF6B35] text-white px-8 py-4 font-mono-t text-sm font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors shadow-md shadow-orange-200">
              {t("admin.unsavedSave")}
            </button>
            <button data-testid="admin-unsaved-discard" onClick={() => resolveUnsaved("discard")}
              className="rounded-full border border-slate-200 px-8 py-3 font-mono-t text-[11px] font-bold uppercase tracking-widest text-slate-500 hover:border-red-400 hover:text-red-500 transition-colors">
              {t("admin.unsavedDiscard")}
            </button>
            <button data-testid="admin-unsaved-cancel" onClick={() => resolveUnsaved("cancel")}
              className="font-mono-t text-[11px] uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors">
              {t("admin.unsavedCancel")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <TopupDialog sim={topupSim} open={!!topupSim} onOpenChange={() => setTopupSim(null)} />
      <RefundDialog order={refundOrder} onClose={() => setRefundOrder(null)}
        onDone={() => api.get("/admin/orders").then(({ data }) => setOrders(data.data || [])).catch(() => {})} />
    </div>
  );
}
